import { msg } from "@omegagrid/localize";
import rp from "@reporting/rp-app-host";
import { Page } from "@reporting/rp-types/wiki";
import type { Sidebar } from "./components";

/**
 * The open wiki sidebar, null when it is not open. A raw map lookup: `host.components.get()` would
 * create (and register) a second sidebar when none is open, and then hand back a promise instead
 * of the component.
 */
export const getSidebar = (): Sidebar | null => {
	return (rp.host.components.components.get('wiki') as Sidebar) ?? null;
}

export const buildWikiImageUrl = (a: number, fileOrId: string | number): string => {
	return `/ms/${a}/wiki/download/${fileOrId}`;
}

export const buildWikiDownloadUrl = (a: number, fileOrId: string | number): string => {
	return `${rp.options.defaultServiceOptions.baseUrl}/${a}/wiki/download/${fileOrId}`;
}

export const isImage = (fileName: string): boolean => {
	const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg'];
	return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
}

/**
 * The segment that tells the address of a company wiki page from one of the application wiki:
 * /{a}/wiki/custom/<path> against /wiki/<path>. The application wiki has no top-level page or folder
 * of that name (its folders are numbered), so the segment is unambiguous.
 */
export const CUSTOM_WIKI_SEGMENT = 'custom';

/** The component id of a page container: `wiki-page-{a}-{id}` (a = 0 for the application wiki). */
export const pageComponentId = (a: number, pageId: number): string => {
	return `wiki-page-${a}-${pageId}`;
}

/** Page name → URL path below /wiki: spaces as '_', folders kept as path segments. */
export const pageNameToPath = (name: string): string => {
	return name.split('/').map(s => encodeURIComponent(s.replace(/ /g, '_'))).join('/');
}

/** URL path below /wiki → page name; the lookup (pageByName) matches '_' and space alike. */
export const pathToPageName = (path: string): string => {
	return path.split('/').map(s => decodeURIComponent(s).replace(/_/g, ' ')).join('/');
}

/**
 * URL path below /wiki → the wiki it names and the page name: `custom/<path>` is a page of the company
 * wiki (`a` = the current company), anything else a page of the application wiki (0).
 */
export const parseWikiPath = (path: string, a: number): { a: number; name: string } => {
	const segments = path.split('/').filter(s => s.length > 0);
	if (segments[0] === CUSTOM_WIKI_SEGMENT) return { a, name: pathToPageName(segments.slice(1).join('/')) };
	return { a: 0, name: pathToPageName(segments.join('/')) };
}

/**
 * Root-relative address of a wiki page, the one a copied link gets: /wiki/<path> for the application
 * wiki, /{a}/wiki/custom/<path> for the company wiki. The anchor goes into the hash: a guest's address
 * never carries the host state and for a user the router tells the two apart (Router.initialAnchor);
 * WikiApplication.openPath scrolls to it once the page opens.
 */
export const buildWikiPageUrl = (a: number, name: string, anchor?: string): string => {
	const url = a ? `/${a}/wiki/${wikiPagePath(a, name)}` : `/wiki/${wikiPagePath(a, name)}`;
	return anchor ? `${url}#${anchor}` : url;
}

/** Puts the full address of a wiki page (buildWikiPageUrl on the current origin) on the clipboard and says so. */
export const copyWikiPageLink = async (a: number, name: string): Promise<void> => {
	await navigator.clipboard.writeText(`${window.location.origin}${buildWikiPageUrl(a, name)}`);
	rp.host.bucket.showAlert({ type: 'success', title: 'Wiki', content: msg('Copied to clipboard') });
}

/** The path of a page below /wiki, the one the address bar shows and parseWikiPath reads: `custom/<path>` for a company wiki page. */
export const wikiPagePath = (a: number, name: string): string => {
	const path = pageNameToPath(name);
	return a ? `${CUSTOM_WIKI_SEGMENT}/${path}` : path;
}

/** Whether a link destination leaves the wiki (a scheme or a protocol-relative URL). */
export const isExternalLink = (href: string): boolean => {
	return /^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('//');
}

/** Undoes percent-encoding where there is some; a malformed sequence leaves the text as it is. */
export const safeDecode = (s: string): string => {
	if (!s.includes('%')) return s;
	try { return decodeURIComponent(s); } catch { return s; }
}

/** A markdown link destination split into the page name and the anchor: `Folder/Page#section`. */
export const splitLinkTarget = (href: string): { name: string; anchor: string } => {
	const i = href.indexOf('#');
	const [name, anchor] = i < 0 ? [href, ''] : [href.slice(0, i), href.slice(i + 1)];
	return { name: safeDecode(name), anchor: safeDecode(anchor) };
}

/** The page a link destination names (with or without its folders, '_' standing for a space), null when there is none. */
export const findPageByName = (a: number, name: string, lang?: string): Promise<Page> => {
	return rp.ms('wiki').get<Page>('pageByName', { name, lang, disable404: true, accounting: a, fields: ['id_wiki_page', 'name'] });
}
